/**
 * admin_ops.service.ts — 관리자 작업(사용자 생성/권한/삭제 등) 클라이언트 헬퍼.
 *
 * GCP 조직정책이 onCall CORS를 막아서, onCall 대신 Firestore 트리거 패턴을 쓴다:
 *   1. admin_ops/{opId} 문서 생성 (status:"pending")
 *   2. 백엔드 processAdminOp(onCreate 트리거)가 처리 후 status:"done"/"error" + result 기록
 *   3. 여기서 onSnapshot으로 결과를 기다렸다가 resolve/reject
 *
 * rules: admin_ops create/read = isAdmin (관리자만). update는 CF Admin SDK 전용.
 */
import { addDoc, collection, onSnapshot, serverTimestamp } from "firebase/firestore";
import { auth, db } from "./firebase";

export type AdminOpAction =
  | "user.create"
  | "user.set_claims"
  | "user.deactivate"
  | "user.delete"
  | "user.reset_pw"
  | "push.test"
  // 12_통합관리_계정기기설계 §6-1 — 법인 관리자는 params.tenant 필수
  | "user.create_with_membership"
  | "user.reactivate"
  // 가입 승인·거부(2026-10-27) — 법인 관리자는 params.tenant = 자기 법인 = 신청 법인
  | "user.approve_signup"
  | "user.reject_signup"
  | "membership.add"
  | "membership.end"
  | "membership.setTenantAdmin"
  | "membership.setRole"
  | "membership.setBiz"
  | "membership.setProfile"
  | "membership.setUnits"
  | "device.revoke"
  | "device.set_auto_login"
  | "session.revoke_all";

const OP_TIMEOUT_MS = 30_000;

/**
 * 관리자 작업 1건 실행. 성공 시 백엔드 result를 반환, 실패 시 throw.
 * @param params 액션별 파라미터:
 *   - user.create      { login_id, name, role, active, password }
 *   - user.set_claims  { targetUid, role, active }
 *   - user.deactivate  { targetUid }
 *   - user.delete      { id }
 *   - user.reset_pw    { id, new_password }
 *   - push.test        {}  (caller_uid 기기로 테스트 푸시 발송)
 */
export function runAdminOp<T = Record<string, unknown>>(
  action: AdminOpAction,
  params: Record<string, unknown>,
): Promise<T> {
  const caller_uid = auth.currentUser?.uid;
  if (!caller_uid) return Promise.reject(new Error("로그인이 필요합니다."));

  return new Promise<T>((resolve, reject) => {
    let settled = false;
    let unsub: (() => void) | null = null;
    let timer = 0;

    const finish = (fn: () => void) => {
      if (settled) return;
      settled = true;
      if (unsub) unsub();
      if (timer) window.clearTimeout(timer);
      fn();
    };

    addDoc(collection(db, "admin_ops"), {
      action,
      params,
      caller_uid,
      status: "pending",
      created_at: serverTimestamp(),
    })
      .then((ref) => {
        timer = window.setTimeout(
          () => finish(() => reject(new Error("작업 시간이 초과되었습니다. 잠시 후 다시 시도하세요."))),
          OP_TIMEOUT_MS,
        );
        unsub = onSnapshot(
          ref,
          (snap) => {
            const d = snap.data();
            if (!d) return;
            if (d.status === "done") finish(() => resolve((d.result ?? null) as T));
            else if (d.status === "error") finish(() => reject(new Error((d.error as string) || "작업에 실패했습니다.")));
          },
          (e) => finish(() => reject(e)),
        );
      })
      .catch((e) => finish(() => reject(e)));
  });
}
